// react
import { useCallback } from 'react';
// @mui
import { Container, Box, Button, Stack, Fab, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
// hooks
import { useAuth, useModal } from '@/hooks';
// services
import { InvestorsService, IncubatorService, SMEService, InstitutionService } from '@/services';
// enums
import { STATUS, USER_GROUP_TYPE } from '@/enums';

const getUserGroupActionLabel = (userGroupType) => {
  switch (userGroupType) {
    case USER_GROUP_TYPE.INVESTOR:
      return 'Apply to Pitch';
    case USER_GROUP_TYPE.INCUBATOR:
      return 'Apply to Incubator';
    case USER_GROUP_TYPE.SME:
    case USER_GROUP_TYPE.INSTITUTION:
      return 'Request Meeting';
  }
};

export default function UserGroupActions({
  userGroupId,
  setShowMessageModal,
  showUserGroupActionButton,
  isStartupMember,
  userGroupType,
  calendlyLink,
  submitted = false,
}) {
  const disabledColor = grey[700];

  const {
    login: { setOpen: setLoginModalOpen },
    applyToPitch: { setOpen: setApplyToPitchModalOpen, setProps: setApplyToPitchModalProps },
    calendlyScheduleMeeting: {
      setOpen: setCalendlyScheduleMeetingModalOpen,
      setProps: setCalendlyScheduleMeetingModalProps,
    },
    selectStartup: { setOpen: setSelectStartupOpen, setProps: setSelectStartupProps },
    createStartup: { setOpen: setCreateStartupModal },
  } = useModal();
  const { user } = useAuth();

  const handleApplyToPitch = async () => {
    if (userGroupType === USER_GROUP_TYPE.INVESTOR) {
      var { success, data = [] } = await InvestorsService.postApplyToPitch(userGroupId);
    }
    if (userGroupType === USER_GROUP_TYPE.INCUBATOR) {
      var { success, data = [] } = await IncubatorService.getApplyToIncubator(userGroupId);
    }

    if (success) {
      setApplyToPitchModalProps({
        userGroupId,
        contentId: null,
        announcementId: null,
        userGroupType,
        applyToPitchData: data,
        dontForward: false,
      });
      setApplyToPitchModalOpen(true);
    }
  };

  const handleScheduleSMEMeeting = useCallback(async () => {
    await SMEService.postScheduleMeeting(userGroupId);
  }, [userGroupId]);

  const handleScheduleInstitutionMeeting = useCallback(async () => {
    await InstitutionService.postScheduleMeeting(userGroupId);
  }, [userGroupId]);

  const handleUserGroupAction = () => {
    if (userGroupType === USER_GROUP_TYPE.SME) {
      setCalendlyScheduleMeetingModalProps({
        calendlyLink,
        contentId: null,
        registered: false,
        onSuccess: handleScheduleSMEMeeting,
      });
      setCalendlyScheduleMeetingModalOpen(true);
    } else if (userGroupType === USER_GROUP_TYPE.INSTITUTION) {
      setCalendlyScheduleMeetingModalProps({
        calendlyLink,
        contentId: null,
        registered: false,
        onSuccess: handleScheduleInstitutionMeeting,
      });
      setCalendlyScheduleMeetingModalOpen(true);
    } else if (isStartupMember) {
      setSelectStartupProps({
        startups:
          user?.userGroupMembers
            ?.filter(
              (item) =>
                item?.userGroup?.userGroupType === USER_GROUP_TYPE.STARTUP &&
                item?.userGroup?.status === STATUS.ACTIVE
            )
            ?.map(({ userGroup }) => userGroup) ?? [],
        onSelect: handleApplyToPitch,
      });
      setSelectStartupOpen(true);
    } else {
      setCreateStartupModal(true);
    }
  };

  const handleSendMessage = () => {
    setShowMessageModal(true);
  };

  return (
    <>
      <Container maxWidth="lg" sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Stack spacing={4}>
            {[
              USER_GROUP_TYPE.INVESTOR,
              USER_GROUP_TYPE.INCUBATOR,
              USER_GROUP_TYPE.SME,
              USER_GROUP_TYPE.INSTITUTION,
            ].includes(userGroupType) && (
              <Stack
                sx={{
                  position: 'fixed',
                  bottom: 85,
                  right: 20,
                  zIndex: '99',
                  border: 'none',
                  outline: 'none',
                  borderRadius: '4px',
                }}
                direction="column"
                alignItems="flex-end"
              >
                {submitted && userGroupType === USER_GROUP_TYPE.INVESTOR && (
                  <Typography variant="subtitle1" color="error">
                    You already submitted to this investor.
                  </Typography>
                )}
                <Fab
                  onClick={
                    user && showUserGroupActionButton
                      ? handleUserGroupAction
                      : () => setLoginModalOpen(true)
                  }
                  sx={{
                    ...(user &&
                      !showUserGroupActionButton && {
                        background: `${disabledColor}!important`,
                      }),
                  }}
                  disabled={user && !showUserGroupActionButton}
                  variant="extended"
                  size="large"
                >
                  <Typography color="white" variant="h6">
                    {getUserGroupActionLabel(userGroupType)}
                  </Typography>
                </Fab>
              </Stack>
            )}

            <Button
              onClick={user ? handleSendMessage : () => setLoginModalOpen(true)}
              className="investorbuilderbuttons"
            >
              <span className="investorbuilderbuttonstext">Message</span>
            </Button>
          </Stack>
        </Box>
      </Container>
    </>
  );
}
